/**
 * When multiple plotlines/markers are very close to one another
 * we group them and call it a cluster.
 *
 * The 'x-value' and 'tooltip' components of markers inside
 * a cluster only appear when you put the mouse over them.
 */

import { CompleteAxis } from './chart-marker'
import { ExtendedPlotline } from './extended-plotline'

class MarkerCluster {
    /**
     * If the provided marker is inside a cluster: highlight
     * this marker and unhighlight all others in the cluster.
     */
    setHighlight(marker: ExtendedPlotline, value: boolean): void {
        if (marker.options.id === 'placeholder') return

        const neighbors = this.closestNeighbors(marker)

        // Markers with no neighbors must be highlighted at
        // al times.
        if (neighbors.length === 0 && marker.focus) return

        if (value) {
            // Unfocus all neighbors
            for (const maker of neighbors) {
                maker.setFocus(false)
            }

            // Focus this one
            marker.setFocus(true)

            // Focus everyone else that is not in a cluster
            // where there is already a focused marker.
            const axis = marker.axis as CompleteAxis
            axis.plotLinesAndBands.forEach(item => {
                if (item.timestamp != null) this.maybeHighlight(item)
            })
        } else {
            // Unfocus this one
            marker.setFocus(false)

            // Maybe focus the first neighbor
            this.maybeHighlight(neighbors[0])
        }
    }

    /**
     * If the provided marker is inside a cluster
     * and in that clsuter there is already a highlithed
     * marker no action is taken.
     *
     * @param marker Marker that may be hilighted
     */
    public maybeHighlight(marker: ExtendedPlotline): void {
        if (marker.options.id === 'placeholder' || marker.svgElem?.visibility === 'hidden') return

        const neighbors = this.closestNeighbors(marker)

        if (neighbors.length === 0 && marker.focus) return
        if (neighbors.some(a => a.focus)) return

        marker.setFocus(true)
    }

    private closestNeighbors(marker: ExtendedPlotline): ExtendedPlotline[] {
        const axis = marker.axis as CompleteAxis
        const inverted = !!axis.chart.inverted
        const neighbors = axis.plotLinesAndBands.filter(otherMarker => {
            const isInvisible = otherMarker.svgElem?.visibility === 'hidden'

            if (
                otherMarker.timestamp == null ||
                isInvisible ||
                otherMarker.options.id === marker.options.id ||
                otherMarker.options.id === 'placeholder'
            ) {
                return false
            }

            // This call might benefit of a cache mechanism (futher studying)
            return this.intersect(marker, otherMarker, inverted)
        })

        return neighbors
    }

    /**
     * Do those two markers overlap ? Markers have areas, do those
     * areas overlap ?
     *
     * @param a Marker a
     * @param b Marker b
     * @param inverted Is the chart inverted ?
     */
    private intersect(a: ExtendedPlotline, b: ExtendedPlotline, inverted: boolean): boolean {
        const lineA = this.getOneDimensionalLine(a, inverted ? 'V' : 'H')
        const lineB = this.getOneDimensionalLine(b, inverted ? 'V' : 'H')

        return (
            between(lineB.a, lineA.a, lineA.b) ||
            between(lineB.b, lineA.a, lineA.b) ||
            //
            between(lineA.a, lineB.a, lineB.b) ||
            between(lineA.b, lineB.a, lineB.b)
        )
    }

    /**
     * Get one dimensional coordinates of a marker
     *
     * @param marker Marker to extract coordinates
     * @param axis X or Y coordinate
     * @returns two one dimensional points representin start an end of this line.
     */
    private getOneDimensionalLine(marker: ExtendedPlotline, axis: 'H' | 'V'): { a: number; b: number } {
        const areaBox = marker.hoverArea.getBBox()
        const tooltipBox = marker.tooltip.getBBox()

        tooltipBox.x = marker.tooltip.translateX
        tooltipBox.y = marker.tooltip.translateY

        if (axis === 'H') {
            const { expandedWidth } = marker.tooltip
            const a = Math.min(areaBox.x, tooltipBox.x)
            const b = Math.max(areaBox.x + areaBox.width, tooltipBox.x + (expandedWidth || tooltipBox.width))

            return { a, b }
        } else {
            const a = Math.min(areaBox.y, tooltipBox.y)
            const b = Math.max(areaBox.y + areaBox.height, tooltipBox.y + tooltipBox.height)

            return { a, b }
        }
    }
}

function between(x: number, min: number, max: number): boolean {
    return x >= min && x <= max
}

export default new MarkerCluster()
